import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Loader2, ArrowLeft, Wallet, Shield, Mail, Phone, Copy, Clock,
  UserPlus, LogIn, Video, GraduationCap, CheckCircle2, XCircle, Compass, User, Fingerprint,
} from "lucide-react";
import api from "@/lib/api";
import { etStamp } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const KIND = {
  invite: { icon: Mail, color: "#2563EB", bg: "rgba(37,99,235,0.10)" },
  discover: { icon: Compass, color: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
  video: { icon: Video, color: "#0891B2", bg: "rgba(8,145,178,0.10)" },
  info: { icon: User, color: "#4060FF", bg: "rgba(64,96,255,0.10)" },
  onboarding: { icon: GraduationCap, color: "#B45309", bg: "rgba(180,83,9,0.10)" },
  signup: { icon: UserPlus, color: "#0F766E", bg: "rgba(15,118,110,0.10)" },
  login: { icon: LogIn, color: "#475569", bg: "rgba(71,85,105,0.10)" },
  account: { icon: Shield, color: "#0F1226", bg: "rgba(15,18,38,0.06)" },
  approved: { icon: CheckCircle2, color: "#00863A", bg: "rgba(0,134,58,0.10)" },
  rejected: { icon: XCircle, color: "#B91C1C", bg: "rgba(220,38,38,0.10)" },
};

const Chip = ({ children, tone = "gray", testid }) => {
  const tones = {
    gray: "bg-slate-100 text-slate-600", blue: "bg-blue-100 text-blue-700",
    green: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-800",
    red: "bg-red-100 text-red-700", violet: "bg-violet-100 text-violet-700",
  };
  return <span data-testid={testid} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
};

const Field = ({ label, children, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-ink-3">{label}</div>
    <div className="text-sm text-ink-1 mt-0.5" data-testid={testid}>{children ?? <span className="text-ink-3">—</span>}</div>
  </div>
);

export default function AdminTrace() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") || params.get("email") || "");
  const [people, setPeople] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);

  const copy = (v) => { if (v) { navigator.clipboard.writeText(v); toast.success("Copied"); } };

  const loadDossier = useCallback(async (email) => {
    setLoadingDossier(true);
    setDossier(null);
    try {
      const { data } = await api.get("/admin/trace", { params: { email } });
      setDossier(data.person);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : d) || "Could not load this person");
    } finally { setLoadingDossier(false); }
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setPeople([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/admin/trace", { params: { q } });
      setPeople(data.people || []);
    } catch { setPeople([]); }
    finally { setSearching(false); }
  }, []);

  // Deep-link: ?email loads a dossier directly; ?q prefills + searches.
  useEffect(() => {
    const em = params.get("email");
    if (em) { setTerm(em); loadDossier(em); return; }
    const q = params.get("q");
    if (q) runSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setDossier(null);
    setParams(term ? { q: term } : {});
    runSearch(term);
  };

  const pick = (email) => { setParams({ email }); loadDossier(email); };
  const back = () => { setDossier(null); setParams(term ? { q: term } : {}); };

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const id = dossier?.identity;
  const src = dossier?.source;
  const sp = dossier?.sponsor;
  const w = dossier?.wallet;

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />Admin · Trace</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">Trace a user</h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">
            Look up anyone — a lead, a member, a mystery sign-up — and see exactly where they came from, who sponsored them, the link they used, and their whole journey with times and IPs.
          </p>
        </div>

        <form onSubmit={onSubmit} className="glass p-3 mb-6 flex items-center gap-2" data-testid="trace-search-form">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }}>
            <Search size={16} className="text-ink-3" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} data-testid="trace-search-input"
              placeholder="Search by name, email or phone…" className="border-0 bg-transparent focus-visible:ring-0 px-0 h-6" />
          </div>
          <Button type="submit" data-testid="trace-search-btn" disabled={searching}>
            {searching ? <Loader2 className="animate-spin" size={14} /> : <><Search size={14} className="mr-1" /> Search</>}
          </Button>
        </form>

        {/* Results list (only when no dossier open) */}
        {!dossier && !loadingDossier && (
          people.length === 0 ? (
            <Card className="p-10 text-center text-ink-3" data-testid="trace-empty">
              {term.trim().length >= 2 && !searching ? "No one matches that search." : "Start typing a name, email or phone to look someone up."}
            </Card>
          ) : (
            <div className="space-y-2" data-testid="trace-results">
              {people.map((p) => (
                <button key={p.email} type="button" onClick={() => pick(p.email)}
                  data-testid={`trace-result-${p.email}`}
                  className="w-full text-left glass p-4 flex items-center gap-4 hover:bg-white/70 transition-colors">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                    style={{ background: "rgba(64,96,255,0.14)", color: "#4060FF" }}>
                    {(p.name || p.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name || "—"}</div>
                    <div className="text-xs text-ink-3 font-mono truncate">{p.email}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {(p.sources || []).map((s) => (
                      <Chip key={s} tone={s === "account" ? "blue" : s === "onboarding" ? "amber" : "violet"}>{s}</Chip>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )
        )}

        {loadingDossier && (
          <Card className="p-10 flex items-center justify-center text-ink-3" data-testid="trace-loading">
            <Loader2 className="animate-spin mr-2" size={16} /> Building the dossier…
          </Card>
        )}

        {/* Dossier */}
        {dossier && !loadingDossier && (
          <div className="space-y-5" data-testid="trace-dossier">
            <button type="button" onClick={back} data-testid="trace-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink-1">
              <ArrowLeft size={15} /> Back to search
            </button>

            {/* Identity */}
            <Card className="p-5" data-testid="trace-identity">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold shrink-0"
                  style={{ background: "rgba(64,96,255,0.14)", color: "#4060FF" }}>
                  {(id.name || id.email || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1">
                  <div className="text-xl font-semibold text-slate-900">{id.name || "—"}</div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-ink-2 mt-1">
                    <span className="inline-flex items-center gap-1 font-mono"><Mail size={13} /> {id.email}
                      <button onClick={() => copy(id.email)} data-testid="trace-copy-email" className="text-ink-3 hover:text-ink-1"><Copy size={12} /></button>
                    </span>
                    {id.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {id.phone}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {id.has_account
                      ? <Chip tone={id.is_super_admin ? "red" : id.role === "admin" ? "red" : id.role === "affiliate_manager" ? "violet" : "blue"} testid="trace-role">
                          {id.is_super_admin ? "Sup Admin" : id.role === "affiliate_manager" ? "Leader" : id.role === "admin" ? "Admin" : "Member"}
                        </Chip>
                      : <Chip tone="violet" testid="trace-role">Lead — no account</Chip>}
                    {id.has_account && <Chip tone={id.status === "Inactive" ? "amber" : "green"}>{id.status || "Active"}</Chip>}
                    {id.pending_approval && <Chip tone="amber">Pending approval</Chip>}
                    {id.archived && <Chip tone="red">Deactivated</Chip>}
                    <Chip tone={id.wallet_synced ? "green" : "amber"} testid="trace-wallet-synced">
                      {id.wallet_synced ? "Wallet synced" : "No wallet yet"}
                    </Chip>
                  </div>
                </div>
              </div>
            </Card>

            {/* Provenance */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-5 space-y-3" data-testid="trace-source">
                <div className="font-semibold text-slate-900 flex items-center gap-2"><Compass size={15} /> Where they came from</div>
                <Field label="Signup source" testid="trace-src-via">{src.onboarded_via || (id.has_account ? "unknown" : "discover lead")}</Field>
                <Field label="Link used" testid="trace-src-link">{src.link_used || (src.registration_code ? `?ref=${src.registration_code}${src.registration_suffix ? `&s=${src.registration_suffix}` : ""}` : null)}</Field>
                {src.created_by_label && <Field label="Created by">{src.created_by_label}</Field>}
              </Card>

              <Card className="p-5 space-y-3" data-testid="trace-sponsor">
                <div className="font-semibold text-slate-900 flex items-center gap-2"><User size={15} /> Sponsor</div>
                <Field label="Sponsor" testid="trace-sponsor-name">{sp.name || <span className="text-amber-700">direct — no sponsor</span>}</Field>
                {sp.email && <Field label="Sponsor email">{sp.email}</Field>}
                {sp.wallet_name && <Field label="Sponsor wallet">{sp.wallet_name}</Field>}
                {sp.link && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-ink-3">Sponsor link</div>
                    <button onClick={() => copy(sp.link)} data-testid="trace-sponsor-link"
                      className="mt-0.5 inline-flex items-center gap-1.5 text-sm font-mono text-blue-700 hover:underline">
                      {sp.link} <Copy size={12} />
                    </button>
                  </div>
                )}
              </Card>

              <Card className="p-5 space-y-3" data-testid="trace-wallet">
                <div className="font-semibold text-slate-900 flex items-center gap-2"><Wallet size={15} /> Wallet</div>
                {w.tiles.length === 0 ? (
                  <Chip tone="amber">No CaryPact wallet synced yet</Chip>
                ) : w.tiles.map((t, i) => (
                  <div key={i} className="text-sm">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                      <Wallet size={11} /> {t.label || t.wallet_name || "wallet"}
                    </span>
                    {t.account_role && <span className="text-ink-3 text-xs ml-2">{t.account_role}</span>}
                  </div>
                ))}
              </Card>
            </div>

            {/* Journey */}
            <Card className="p-5" data-testid="trace-journey">
              <div className="font-semibold text-slate-900 flex items-center gap-2 mb-4"><Clock size={15} /> Journey ({dossier.journey.length})</div>
              {dossier.journey.length === 0 ? (
                <div className="text-ink-3 text-sm">No recorded activity yet.</div>
              ) : (
                <ol className="relative border-l-2 border-black/5 ml-3 space-y-5">
                  {dossier.journey.map((e, i) => {
                    const k = KIND[e.kind] || KIND.account;
                    const Ic = k.icon;
                    return (
                      <li key={i} className="ml-6 relative" data-testid={`trace-journey-event-${i}`}>
                        <span className="absolute -left-[38px] w-8 h-8 rounded-full flex items-center justify-center ring-4 ring-white"
                          style={{ background: k.bg, color: k.color }}>
                          <Ic size={14} />
                        </span>
                        <div className="text-sm font-medium text-slate-900">{e.label}</div>
                        <div className="text-[12px] text-ink-3 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          <span className="inline-flex items-center gap-1"><Clock size={11} /> {e.at ? `${etStamp(e.at)} ET` : "—"}</span>
                          {e.ip && <span className="inline-flex items-center gap-1 font-mono"><Fingerprint size={11} /> {e.ip}</span>}
                        </div>
                        {e.detail && <div className="text-[12px] text-ink-2 mt-0.5 break-words">{e.detail}</div>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
